import { Injectable } from '@angular/core';
import dayjs from 'dayjs';

export interface DurationOption {
  label: string;
  value: string;
}

export interface DateRange {
  fromDate: Date;
  toDate: Date;
  maxFromDate: Date;
  maxToDate: Date;
}

interface DurationConfig {
  unit: 'day' | 'month';
  amount: number;
}

@Injectable({
  providedIn: 'root'
})
export class DateRangeService {
  
  readonly durationOptions: DurationOption[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'One Day', value: 'oneDay' },
    { label: 'Seven Days', value: 'sevenDays' },
    { label: 'One Month', value: 'oneMonth' },
    { label: 'Three Months', value: 'threeMonths' },
    { label: 'Custom', value: 'custom' }
  ];

  calculateDateRange(duration: string): DateRange {
    const today = dayjs();
    
    switch (duration) {
      case 'today':
        return {
          fromDate: today.startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.startOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
        
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        return {
          fromDate: yesterday.startOf('day').toDate(),
          toDate: yesterday.endOf('day').toDate(),
          maxFromDate: yesterday.startOf('day').toDate(),
          maxToDate: yesterday.endOf('day').toDate()
        };
        
      case 'oneDay':
        return {
          fromDate: today.startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.startOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
        
      case 'sevenDays':
        return {
          fromDate: today.subtract(6, 'day').startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.subtract(6, 'day').startOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
        
      case 'oneMonth':
        return {
          fromDate: today.subtract(1, 'month').startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.subtract(1, 'month').startOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
        
      case 'threeMonths':
        return {
          fromDate: today.subtract(3, 'month').startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.subtract(3, 'month').startOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
        
      case 'custom':
      default:
        return {
          fromDate: today.startOf('day').toDate(),
          toDate: today.endOf('day').toDate(),
          maxFromDate: today.endOf('day').toDate(),
          maxToDate: today.endOf('day').toDate()
        };
    }
  }

  adjustDateRangeFromChange(fromDate: Date, duration: string): Date {
    if (this.isDatePickerDisabled(duration) || this.isCustomRange(duration)) {
      return fromDate;
    }

    const fromDay = dayjs(fromDate);
    const config = this.getDurationConfig(duration);
    
    if (config.unit === 'day') {
      if (config.amount === 1) {
        return fromDay.endOf('day').toDate();
      } else {
        return fromDay.add(config.amount - 1, 'day').endOf('day').toDate();
      }
    } else if (config.unit === 'month') {
      return fromDay.add(config.amount, 'month').endOf('day').toDate();
    }
    
    return fromDate;
  }

  adjustDateRangeToChange(toDate: Date, duration: string): Date {
    if (this.isDatePickerDisabled(duration) || this.isCustomRange(duration)) {
      return toDate;
    }

    const toDay = dayjs(toDate);
    const config = this.getDurationConfig(duration);
    
    if (config.unit === 'day') {
      if (config.amount === 1) {
        return toDay.startOf('day').toDate();
      } else {
        return toDay.subtract(config.amount - 1, 'day').startOf('day').toDate();
      }
    } else if (config.unit === 'month') {
      return toDay.subtract(config.amount, 'month').startOf('day').toDate();
    }
    
    return toDate;
  }

  isCustomRange(duration: string): boolean {
    return duration === 'custom';
  }

  isDatePickerDisabled(duration: string): boolean {
    return duration === 'today' || duration === 'yesterday';
  }

  validateDateRange(fromDate: Date, toDate: Date): string | null {
    if (!fromDate || !toDate) {
      return 'Please select a valid date range.';
    }
    
    const from = dayjs(fromDate);
    const to = dayjs(toDate);
    
    if (from.isAfter(to)) {
      return 'From Date cannot be after To Date.';
    }
    
    if (from.isAfter(dayjs().endOf('day')) || to.isAfter(dayjs().endOf('day'))) {
      return 'Future dates are not allowed.';
    }
    
    return null;
  }

  private getDurationConfig(duration: string): DurationConfig {
    switch (duration) {
      case 'oneDay': return { unit: 'day', amount: 1 };
      case 'sevenDays': return { unit: 'day', amount: 7 };
      case 'oneMonth': return { unit: 'month', amount: 1 };
      case 'threeMonths': return { unit: 'month', amount: 3 };
      default: return { unit: 'day', amount: 1 };
    }
  }
}